import "./VideoPlayer.css";
import { useState, useRef, useEffect, ReactNode, cloneElement, Children } from "react";
import { Button } from "@tennr/frontend/components/Button";
import Player from '@vimeo/player';

const createHashCode = (s:string) =>
  s.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);

export interface VideoPlayerProps {
  source: string;
  poster: string;
  mobileSource?: string;
  mobilePoster?: string;
  ref?: any;
  mimetype?: string;  
  accentColor?: string;
  controls?: boolean;
  children?: ReactNode;
  className?: string;
};


export const VideoPlayer = (props: VideoPlayerProps) => { 
  const [overlayClosed, setOverlayClosed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const playerRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLElement>(null);

  let player:any;
  const isVimeo: boolean = props.source.includes("vimeo.com/");
  const hash: number = isVimeo ? createHashCode(props.source) : 0;
  
  const closeOverlay = function () {
    playerRef?.current?.play();
    setOverlayClosed(true);
  };

  const videoObserver = new IntersectionObserver(
    ([entry]) => {
      // Watch for video to leave screen to pause and close overlay
      if (!entry.isIntersecting) {
        setOverlayClosed(false);
        playerRef?.current?.pause();
      }
    },
    { threshold: 0.3 }
  );
  const loadVimeoPlayer =  async function (videoUrl:string){
    try {      
      player = new Player(`vimeo-player-${hash}`, { url: `${videoUrl}`} );  
      player.setColors(['#9B4E45', '#9B4E45', '123', '000'])    
      player.on('loaded', ()=>{
        playerRef.current = player;        
        videoObserver.observe(player.element);
      });
      player.on('pause', ()=>{
        setOverlayClosed(false)
        setTimeout(()=>{player.setCurrentTime(0)},600)
        
      });
    } catch(error){
      console.error(error)
    }
    
  }
  

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
  
    const handleResize = async (e?: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : e?.matches ?? mediaQuery.matches;
      setIsMobile(matches);
  
      const source = matches && props.mobileSource ? props.mobileSource : props.source;
      const poster = matches && props.mobilePoster ? props.mobilePoster : props.poster;
  
      if (isVimeo) {
        // If there was an old player, destroy it
        if (playerRef.current) {
          await playerRef.current.destroy();
        }
  
        // Create new player
        const newPlayer = new Player(`vimeo-player-${hash}`, { url: source });
        playerRef.current = newPlayer;
  
        newPlayer.setColor('#9B4E45');
  
        newPlayer.on('loaded', () => {
          videoObserver.observe(newPlayer.element);
        });
  
        newPlayer.on('pause', () => {
          setOverlayClosed(false);
          setTimeout(() => newPlayer.setCurrentTime(0), 600);
        });
      }
  
      if (overlayRef.current) {
        overlayRef.current.style.backgroundImage = `url(${poster})`;
      }
    };

    handleResize(mediaQuery);
  
    mediaQuery.addEventListener("change", handleResize);
    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);
  

  return (
    <div className={`video-wrapper${ props?.className ? " "+props?.className : '' }`} ref={props?.ref } >
      <div className={`video-overlay ${overlayClosed ? "closed" : ""}`}
        style={{ backgroundImage: `url("${props?.poster}")` }} ref={overlayRef}
      >
        <div className="overlay-background" style={{ backgroundImage: `url('/img/grain.png')`, opacity: 0.32 }} ></div>
        { props?.children ? 
          Children.map(props.children, (child) => 
            cloneElement(child, { onClick: closeOverlay })
          ) :
          <Button
            text=" "
            iconPath="/svg/play.svg"
            iconPos="left"
            minWidth={113}
            minHeight={64}
            backgroundColorLight="#FBFBF9"
            backgroundColorDark="#FBFBF9"
            textColorLight="#5F4037"
            textColorDark="#5F4037"
            onClick={() => closeOverlay()}
          />
        }
      </div>
      <div className={`video-player ${ (isVimeo) ? 'embed' : ''}`} id={ (isVimeo) &&  `vimeo-player-${hash}`}>        
        { !isVimeo &&            
          <video
            controls={props.controls}
            controlsList="nodownload"
            preload="metadata"
            poster={props.poster}
            ref={playerRef}
          >
            <source src={props.source} type={props.mimetype}></source>
          </video>
}
      </div>
      </div>
  );
};
